import React, { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import QRCode from 'qrcode';
import AppConst from "../apis/appconst";
import { loadFontAsBase64 } from "../loadFont";

const QRReportDownload = () => {
  const { labRegId } = useParams();
  const [searchParams] = useSearchParams();
  
  const [status, setStatus] = useState('loading'); // loading, generating, success, error
  const [message, setMessage] = useState('Fetching report data...');
  const [progress, setProgress] = useState(0);
  const [patientName, setPatientName] = useState('');
  const [testCount, setTestCount] = useState(0);

  // Store fetched data for PDF generation
  const reportDataRef = useRef(null);

  useEffect(() => {
    const regId = labRegId || searchParams.get('lab_reg_id');
    if (regId) {
      fetchAndGeneratePDF(regId);
    } else {
      setStatus('error');
      setMessage('No lab registration ID provided');
    }
  }, [labRegId, searchParams]);

  const fetchAndGeneratePDF = async (regId) => {
    try {
      setStatus('loading');
      setMessage('Fetching report data...');
      setProgress(10);

      // Fetch report data from backend
      const fd = new FormData();
      fd.append('lab_reg_id', regId);

      const response = await axios.post(
        AppConst.BASE_URL + 'testreportcon/get_report_for_qr',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setProgress(30);

      if (!response.data.status) {
        setStatus('error');
        setMessage(response.data.message || 'Failed to fetch report');
        return;
      }

      const { patient, tests, config, departments, signatures } = response.data;

      if (!patient) {
        setStatus('error');
        setMessage('Patient not found');
        return;
      }

      setPatientName(patient.lab_reg_name || 'Unknown');

      if (!tests || tests.length === 0) {
        setStatus('error');
        setMessage('No approved non-radiology tests found for this patient');
        return;
      }

      setTestCount(tests.length);
      reportDataRef.current = { patient, tests, config, departments, signatures };

      setProgress(50);
      setStatus('generating');
      setMessage('Generating PDF...');

      // Generate PDF
      await generatePDF(patient, tests, config, departments, signatures);

      setProgress(100);
      setStatus('success');
      setMessage('Report downloaded successfully!');

    } catch (error) {
      console.error('Error:', error);
      setStatus('error');
      setMessage('Failed to generate report: ' + (error.message || 'Unknown error'));
    }
  };

  // ========== PDF GENERATION FUNCTIONS ==========

  const imageUrlToBase64 = async (url) => {
    if (!url) return null;
    try {
      const fullUrl = url.startsWith('http') ? url : AppConst.BASE_URL + url;
      const response = await fetch(fullUrl, { cache: 'no-cache', mode: 'cors' });
      if (!response.ok) return null;
      const blob = await response.blob();
      if (!blob.type.startsWith('image/')) return null;
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.warn("Image load failed:", url, err);
      return null;
    }
  };

  const formatDateTime12h = (dateTimeStr) => {
    if (!dateTimeStr) return "";
    try {
      const d = new Date(dateTimeStr);
      if (isNaN(d.getTime())) return dateTimeStr;
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      let hours = d.getHours();
      const minutes = String(d.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      return `${day}-${month}-${year} ${String(hours).padStart(2,"0")}:${minutes} ${ampm}`;
    } catch (e) {
      return dateTimeStr;
    }
  };

  const getAgeDisplay = (p) => {
    if (!p) return 'N/A';
    const y = Number(p.lab_reg_age_year || 0);
    const m = Number(p.lab_reg_age_month || 0);
    const d = Number(p.lab_reg_age_day || 0);
    if (y === 0 && m === 0 && d === 0) return 'N/A';
    const parts = [];
    if (y > 0) parts.push(`${y}Y`);
    if (m > 0) parts.push(`${m}M`);
    if (d > 0) parts.push(`${d}D`);
    return parts.join(' ');
  };

  const get_param_val = (tmp) => {
    if (!tmp) return "";
    const type = (tmp.test_param_type || "").trim();
    if (type === "Numeric") {
      const from = tmp.test_param_from_range ?? "";
      const to = tmp.test_param_to_range ?? "";
      if (from === "" && to === "") return "";
      if (from !== "" && to !== "") return `${from} - ${to}`;
      return from || to;
    }
    if (type === "Boolean") {
      return tmp.test_param_from_range || "Yes / No";
    }
    return tmp.test_param_from_range || "";
  };

  const formatCellValue = (htmlString) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;
    const table = tempDiv.querySelector("table");
    if (table) {
      let rows = [];
      for (const tr of table.querySelectorAll("tr")) {
        let cells = [];
        for (const td of tr.querySelectorAll("td")) {
          cells.push(td.innerText.trim());
        }
        rows.push(cells.join(" | "));
      }
      return rows.join("\n");
    }
    return tempDiv.innerText.trim();
  };

  const addHeaderAndFooter = async (doc, config) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Watermark
    if (config?.watermark) {
      try {
        const watermarkBase64 = await imageUrlToBase64(config.watermark);
        if (watermarkBase64 && typeof doc.setGState === 'function') {
          doc.setGState(new doc.GState({ opacity: 0.08 }));
          doc.addImage(watermarkBase64, 'PNG', (pageWidth - 120) / 2, (pageHeight - 120) / 2, 120, 120);
          doc.setGState(new doc.GState({ opacity: 1 }));
        }
      } catch (e) {}
    }

    // Header
    if (config?.header_logo) {
      try {
        const headerBase64 = await imageUrlToBase64(config.header_logo);
        if (headerBase64) doc.addImage(headerBase64, 0, 0, pageWidth, 30);
      } catch (e) {}
    }

    // Footer
    if (config?.footer_logo) {
      try {
        const footerBase64 = await imageUrlToBase64(config.footer_logo);
        if (footerBase64) doc.addImage(footerBase64, 0, pageHeight - 13, pageWidth, 13);
      } catch (e) {}
    }
  };

  const printPatientDetails = async (doc, patient, startY = 40) => {
    try {
      const pageWidth = doc.internal.pageSize.getWidth();
      const boxLeft = 10;
      const boxTop = startY - 2;
      const boxWidth = pageWidth - boxLeft * 2;
      const boxHeight = 24;

      // Draw box
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.2);
      doc.rect(boxLeft, boxTop, boxWidth, boxHeight);

      // QR Code
      const qrSize = 20;
      const qrX = boxLeft + boxWidth - qrSize - 3;
      const qrY = boxTop + (boxHeight - qrSize) / 10;
      try {
        const qrText = `${patient.lab_reg_uhid || ''} | ${patient.lab_reg_name || ''}`;
        const qrDataUrl = await QRCode.toDataURL(qrText, { width: 200 });
        doc.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize);
      } catch (e) {}

      // Patient info
      const dateParts = String(patient.lab_reg_date || "").split("-");
      const formattedDate = dateParts.length >= 3 
        ? `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}` 
        : (patient.lab_reg_date || "");
      const printedOn = formatDateTime12h(new Date().toISOString());

      const leftColLabel = boxLeft + 4;
      const leftColValue = leftColLabel + 36;
      const rightColLabel = boxLeft + Math.round(boxWidth * 0.5) + 4;
      const rightColValue = rightColLabel + 36;

      let yPos = boxTop + 5;
      doc.setFontSize(10);

      // Row 1
      doc.setFont('helvetica', 'bold'); doc.text("UHID", leftColLabel, yPos);
      doc.setFont('helvetica', 'normal'); doc.text(`: ${patient.lab_reg_uhid || ''}`, leftColValue, yPos);
      doc.setFont('helvetica', 'bold'); doc.text("Patient ID", rightColLabel, yPos);
      doc.setFont('helvetica', 'normal'); doc.text(`: 00${patient.lab_reg_id || ''}`, rightColValue, yPos);
      yPos += 4;

      // Row 2
      const titlePrefix = patient.lab_reg_name_title ? String(patient.lab_reg_name_title).trim() + ' ' : '';
      doc.setFont('helvetica', 'bold'); doc.text("Patient Name", leftColLabel, yPos);
      doc.setFont('helvetica', 'normal'); doc.text(`: ${titlePrefix}${patient.lab_reg_name || ''}`, leftColValue, yPos);
      doc.setFont('helvetica', 'bold'); doc.text("Registered On", rightColLabel, yPos);
      doc.setFont('helvetica', 'normal'); doc.text(`: ${formattedDate}`, rightColValue, yPos);
      yPos += 4;

      // Row 3
      doc.setFont('helvetica', 'bold'); doc.text("Age / Gender", leftColLabel, yPos);
      doc.setFont('helvetica', 'normal'); doc.text(`: ${getAgeDisplay(patient)} / ${patient.lab_reg_gender || ''}`, leftColValue, yPos);
      doc.setFont('helvetica', 'bold'); doc.text("Collected At", rightColLabel, yPos);
      doc.setFont('helvetica', 'normal'); doc.text(`: ${patient.lab_collected_at || ''}`, rightColValue, yPos);
      yPos += 4;

      // Row 4
      doc.setFont('helvetica', 'bold'); doc.text("Referred By", leftColLabel, yPos);
      doc.setFont('helvetica', 'normal'); doc.text(`: ${patient.lab_reg_referral || 'N/A'}`, leftColValue, yPos);
      doc.setFont('helvetica', 'bold'); doc.text("Printed On", rightColLabel, yPos);
      doc.setFont('helvetica', 'normal'); doc.text(`: ${printedOn}`, rightColValue, yPos);

      return boxTop + boxHeight + 10;
    } catch (err) {
      console.error("printPatientDetails error:", err);
      return startY + 30;
    }
  };

  const addSignatures = async (doc, signData) => {
    if (!signData) return;
    
    const finalY = doc.internal.pageSize.getHeight() - 45;

    // Left: Lab Technician
    if (signData.sign_tech_sign) {
      try {
        const techSignBase64 = await imageUrlToBase64(AppConst.BASE_URL + signData.sign_tech_sign);
        if (techSignBase64) {
          doc.addImage(techSignBase64, 'PNG', 14, finalY, 30, 15);
        }
      } catch (e) {}
    }
    if (signData.sign_tech_name) {
      doc.setFontSize(9).setFont('helvetica', 'normal').text(signData.sign_tech_name, 14, finalY + 20);
      doc.setFontSize(9).setFont('helvetica', 'bold').text("Lab Technician", 14, finalY + 24);
    }

    // Right: Doctor
    if (signData.sign_doc_sign) {
      try {
        const docSignBase64 = await imageUrlToBase64(AppConst.BASE_URL + signData.sign_doc_sign);
        if (docSignBase64) {
          doc.addImage(docSignBase64, 'PNG', 166, finalY, 30, 15);
        }
      } catch (e) {}
    }
    if (signData.sign_doc_name) {
      doc.setFontSize(11).setFont('helvetica', 'bold').text(signData.sign_doc_name, 196, finalY + 20, { align: 'right' });
      const designation = signData.sign_doc_spec || 'Consultant Pathologist';
      doc.setFontSize(9).setFont('helvetica', 'bold').text(designation, 196, finalY + 24, { align: 'right' });
    }
  };

  const drawHtmlContent = async (doc, htmlString, initialX, initialY, options) => {
    const { maxWidth, addNewPage, pageHeight, pageBuffer } = options;
    let x = initialX;
    let y = initialY;

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;
    const cleanText = tempDiv.innerText.trim();
    
    if (!cleanText) return y;

    const lines = doc.splitTextToSize(cleanText, maxWidth);
    
    for (const line of lines) {
      // ✅ UPDATED: Use pageBuffer (which is now SIGNATURE_AREA_HEIGHT) for checking
      if (y > pageHeight - pageBuffer) {
        y = await addNewPage();
      }
      doc.setFontSize(9).setFont('helvetica', 'normal');
      doc.text(line, x, y);
      y += 5;
    }

    return y;
  };

  const generatePDF = async (patient, tests, config, departments, signatures) => {
    const doc = new jsPDF();

    // Try to load custom font
    try {
      const fontBase64 = await loadFontAsBase64("/DejaVuSans.ttf");
      doc.addFileToVFS("DejaVuSans.ttf", fontBase64);
      doc.addFont("DejaVuSans.ttf", "DejaVu", "normal");
      doc.setFont("DejaVu");
    } catch (error) {
      console.warn("Font loading failed:", error);
    }

    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageBuffer = 35;
    
    // ✅ Constants for signature area protection
    const SIGNATURE_AREA_HEIGHT = 50;
    const MIN_TEST_CONTENT_HEIGHT = 45;

    // ✅ NEW: Track current page and department per page
    let currentPageNumber = 1;
    let lastPrintedDeptOnCurrentPage = null;
    let currentTestDeptName = null;

    // ✅ UPDATED: addNewPage resets department tracking
    const addNewPage = async () => {
      doc.addPage();
      currentPageNumber++;
      lastPrintedDeptOnCurrentPage = null; // ✅ Reset so department header prints on new page
      await addHeaderAndFooter(doc, config);
      return await printPatientDetails(doc, patient, 35);
    };

    // ✅ NEW: Helper function to print department header
    const printDepartmentHeader = (deptName) => {
      doc.setFillColor(180, 220, 250);
      doc.rect(10, yPos - 5, 190, 8, 'FD');
      doc.setFontSize(12).setFont('helvetica', 'bold').setTextColor(0, 0, 0);
      doc.text(deptName, 105, yPos, { align: 'center' });
      yPos += 5;
      lastPrintedDeptOnCurrentPage = deptName;
    };

    // First page setup
    await addHeaderAndFooter(doc, config);
    let yPos = await printPatientDetails(doc, patient, 35);

    // ✅ UPDATED: Draw table helper with department reprinting
    const drawMainTable = async (rows, needsHead) => {
      if (rows.length <= 1) return;

      const tableHeightEstimate = rows.length * 8 + (needsHead ? 10 : 0);
      
      if (yPos + tableHeightEstimate > pageHeight - SIGNATURE_AREA_HEIGHT) {
        yPos = await addNewPage();
        
        // ✅ On new page, reprint department header if needed
        if (currentTestDeptName && currentTestDeptName !== lastPrintedDeptOnCurrentPage) {
          printDepartmentHeader(currentTestDeptName);
        }
        
        needsHead = true;
      }

      autoTable(doc, {
        startY: yPos,
        head: needsHead ? [["TEST NAME", "RESULT", "UNITS", "NORMAL RANGES", "METHOD"]] : null,
        body: rows,
        theme: 'plain',
        headStyles: { fillColor: [180, 220, 250], textColor: 0, fontStyle: 'bold', halign: 'center' },
        styles: { fontSize: 9, cellPadding: 2, valign: 'middle' },
        columnStyles: {
          0: { cellWidth: 48 },
          1: { cellWidth: 38, halign: 'center' },
          2: { cellWidth: 22, halign: 'center' },
          3: { cellWidth: 40, halign: 'center' },
          4: { cellWidth: 43, halign: 'center' }
        },
        margin: { left: 10, right: 10 }
      });

      yPos = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 4 : yPos + 4;
    };

    // Process each test
    for (const test of tests) {
      
      // ✅ Check if we have enough space for minimum test content before starting
      if (yPos + MIN_TEST_CONTENT_HEIGHT > pageHeight - SIGNATURE_AREA_HEIGHT) {
        yPos = await addNewPage();
      }

      // Department heading
      let deptName = "PATHOLOGY";
      if (test.params && test.params.length > 0 && departments) {
        deptName = departments[test.params[0].test_cat] || "PATHOLOGY";
      }

      // ✅ Store current test's department for reprinting on new pages
      currentTestDeptName = deptName;

      // ✅ UPDATED: Print department header only if different from last printed on THIS page
      let addTableHead = false;
      if (deptName && deptName !== lastPrintedDeptOnCurrentPage) {
        // Check space for department header + test content
        if (yPos + 15 + MIN_TEST_CONTENT_HEIGHT > pageHeight - SIGNATURE_AREA_HEIGHT) {
          yPos = await addNewPage();
        }
        printDepartmentHeader(deptName);
        addTableHead = true;
      }

      // ✅ UPDATED: Before drawing test name row, check if we have space
      const spaceForTestBlock = 40;
      if (yPos + spaceForTestBlock > pageHeight - SIGNATURE_AREA_HEIGHT) {
        yPos = await addNewPage();
        
        // ✅ On new page, reprint department header
        if (deptName && deptName !== lastPrintedDeptOnCurrentPage) {
          printDepartmentHeader(deptName);
        }
        
        addTableHead = true;
      }

      let paramsRows = [];
      
      // Main test name row
      const mainTestNameRow = [{
        content: test.lab_item_test_name || test.test_name,
        colSpan: 5,
        styles: { fontStyle: 'bold', textColor: [0, 0, 0], cellPadding: { top: 2, bottom: 2, left: 2 } }
      }];
      paramsRows.push(mainTestNameRow);

      let hasResultInCurrentTable = false;

      // Process params
      for (const param of (test.params || [])) {
        const rawParamName = String(param.test_param_name || "");
        const isPdfStarred = /^\*.+\*$/.test(rawParamName.trim());
        const pdfParamName = isPdfStarred ? rawParamName.trim().replace(/^\*(.+)\*$/, '$1') : rawParamName;

        // Editor type - render separately
        if (param.test_param_type === "Editor") {
          if (hasResultInCurrentTable) {
            await drawMainTable(paramsRows, addTableHead);
            paramsRows = [mainTestNameRow];
            hasResultInCurrentTable = false;
            addTableHead = false;
          }

          // ✅ UPDATED: Check against signature area with department reprinting
          if (yPos + 12 > pageHeight - SIGNATURE_AREA_HEIGHT) {
            yPos = await addNewPage();
            if (currentTestDeptName && currentTestDeptName !== lastPrintedDeptOnCurrentPage) {
              printDepartmentHeader(currentTestDeptName);
            }
          }
          yPos += 4;

          doc.setFontSize(11).setFont('helvetica', 'bold');
          doc.text(pdfParamName, 16, yPos);
          yPos += 7;

          const editorContent = String(param.result || "").trim();
          if (editorContent) {
            yPos = await drawHtmlContent(doc, editorContent, 16, yPos, {
              maxWidth: 178,
              addNewPage: async () => {
                const newY = await addNewPage();
                if (currentTestDeptName && currentTestDeptName !== lastPrintedDeptOnCurrentPage) {
                  printDepartmentHeader(currentTestDeptName);
                }
                return yPos = newY;
              },
              pageHeight,
              pageBuffer: SIGNATURE_AREA_HEIGHT
            });
            yPos += 4;
          }
          yPos += 6;
          continue;
        }

        // Non-Editor params
        const hasResult = param.result !== undefined && param.result !== null && String(param.result).trim() !== "";
        if (!hasResult && !isPdfStarred) continue;

        let resultWithFlag = hasResult ? String(param.result).trim() : "";
        let resultStyles = { halign: 'center', textColor: [0, 0, 0] };

        // Check for high/low values
        if (hasResult) {
          const rangeValue = formatCellValue(get_param_val(param));
          const match = String(rangeValue).match(/([\d.]+)\s*-\s*([\d.]+)/);
          if (match) {
            const min = parseFloat(match[1]);
            const max = parseFloat(match[2]);
            const resultNum = parseFloat(resultWithFlag);
            if (!isNaN(resultNum)) {
              if (resultNum > max) {
                resultWithFlag = `${resultWithFlag} H`;
                resultStyles = { ...resultStyles, fontStyle: 'bold' };
              } else if (resultNum < min) {
                resultWithFlag = `${resultWithFlag} L`;
                resultStyles = { ...resultStyles, fontStyle: 'bold' };
              }
            }
          }
        }

        paramsRows.push([
          { content: pdfParamName, styles: isPdfStarred ? { fontStyle: 'bold' } : {} },
          { content: resultWithFlag || "", styles: resultStyles },
          param.test_range_type || "",
          formatCellValue(get_param_val(param)) || "",
          param.test_param_method || ""
        ]);

        hasResultInCurrentTable = true;
      }

      // Draw remaining params
      if (hasResultInCurrentTable) {
        await drawMainTable(paramsRows, addTableHead);
      }

      // Sample type
      if (test.params?.[0]?.test_sample_type) {
        // ✅ UPDATED: Check against signature area with department reprinting
        if (yPos + 15 > pageHeight - SIGNATURE_AREA_HEIGHT) {
          yPos = await addNewPage();
          if (currentTestDeptName && currentTestDeptName !== lastPrintedDeptOnCurrentPage) {
            printDepartmentHeader(currentTestDeptName);
          }
        }
        autoTable(doc, {
          startY: yPos,
          body: [[{
            content: `Sample Type : ${test.params[0]?.test_sample_type}`,
            colSpan: 5,
            styles: { fontStyle: 'bold', textColor: [80, 80, 80] }
          }]],
          theme: 'plain',
          styles: { fontSize: 8, cellPadding: 1, valign: 'middle' },
          margin: { left: 14, right: 14 },
        });
        yPos = doc.lastAutoTable.finalY + 6;
      } else {
        yPos += 4;
      }

      // ✅ UPDATED: Interpretation, Impression, Notes - with department reprinting
      const drawBorderedSection = async (title, content) => {
        if (!content) return;
        const cleanText = String(content).replace(/<[^>]+>/g, '').trim();
        if (!cleanText) return;

        const lines = doc.splitTextToSize(cleanText, 178);
        const boxHeight = (lines.length * 5) + 12;

        // ✅ Check if box would overlap signature area, move to new page if needed
        if (yPos + boxHeight > pageHeight - SIGNATURE_AREA_HEIGHT) {
          yPos = await addNewPage();
          if (currentTestDeptName && currentTestDeptName !== lastPrintedDeptOnCurrentPage) {
            printDepartmentHeader(currentTestDeptName);
          }
        }

        doc.setDrawColor(0, 0, 0).setLineWidth(0.2).rect(14, yPos, 182, boxHeight);
        doc.setFontSize(9).setFont('helvetica', 'bold').text(title, 16, yPos + 5);
        doc.setFontSize(8).setFont('helvetica', 'normal').text(lines, 16, yPos + 11);
        yPos += boxHeight + 6;
      };

      await drawBorderedSection('Interpretation', test.interpretation);
      await drawBorderedSection('Impression', test.impression);
      await drawBorderedSection('Notes:', test.notes);
    }

    // End of Report - check against signature area
    if (yPos + 20 > pageHeight - SIGNATURE_AREA_HEIGHT) {
      yPos = await addNewPage();
    }
    doc.setFontSize(12).setFont('helvetica', 'bold');
    doc.text('*** End of Report ***', pageWidth / 2, yPos + 10, { align: 'center' });

    // Add signatures and page numbers
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      
      // Signatures (use default pathology signature since we excluded radiology)
      const signData = signatures?.['32'] || signatures?.['default'] || Object.values(signatures || {})[0];
      await addSignatures(doc, signData);

      // ✅ Page number moved higher to avoid footer overlap
      doc.setFontSize(8).setTextColor(0, 0, 0);
      doc.text(`Page ${i} of ${totalPages}`, pageWidth - 14, pageHeight - 18, { align: 'right' });
    }

    // Download PDF
    const fileName = `Lab_Report_${patient.lab_reg_uhid || patient.lab_reg_id}_${Date.now()}.pdf`;
    doc.save(fileName);
  };

  // ========== RENDER ==========

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      backgroundColor: '#f5f5f5',
      fontFamily: 'Arial, sans-serif'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '40px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        textAlign: 'center',
        maxWidth: '500px',
        width: '100%'
      }}>
        {/* Logo/Header */}
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#405189', margin: 0 }}>Lab Report Download</h2>
        </div>

        {/* Loading State */}
        {(status === 'loading' || status === 'generating') && (
          <div>
            <div style={{
              width: '60px',
              height: '60px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #405189',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }} />
            <p style={{ color: '#666', marginBottom: '15px' }}>{message}</p>
            
            {/* Progress Bar */}
            <div style={{
              width: '100%',
              height: '8px',
              backgroundColor: '#e0e0e0',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${progress}%`,
                height: '100%',
                backgroundColor: '#405189',
                transition: 'width 0.3s ease'
              }} />
            </div>
            <p style={{ color: '#999', fontSize: '14px', marginTop: '10px' }}>{progress}%</p>
          </div>
        )}

        {/* Success State */}
        {status === 'success' && (
          <div>
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#28a745',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <span style={{ color: 'white', fontSize: '40px' }}>✓</span>
            </div>
            <h3 style={{ color: '#28a745', marginBottom: '10px' }}>Download Complete!</h3>
            <p style={{ color: '#666' }}>{message}</p>
            
            {patientName && (
              <div style={{
                marginTop: '20px',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px'
              }}>
                <p style={{ margin: '5px 0', color: '#333' }}>
                  <strong>Patient:</strong> {patientName}
                </p>
                <p style={{ margin: '5px 0', color: '#333' }}>
                  <strong>Tests Included:</strong> {testCount}
                </p>
                <p style={{ margin: '5px 0', color: '#888', fontSize: '12px' }}>
                  (Radiology tests excluded)
                </p>
              </div>
            )}

            <button
              onClick={() => {
                if (reportDataRef.current) {
                  const { patient, tests, config, departments, signatures } = reportDataRef.current;
                  generatePDF(patient, tests, config, departments, signatures);
                }
              }}
              style={{
                marginTop: '20px',
                padding: '12px 30px',
                backgroundColor: '#405189',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Download Again
            </button>
          </div>
        )}

        {/* Error State */}
        {status === 'error' && (
          <div>
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#dc3545',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <span style={{ color: 'white', fontSize: '40px' }}>✕</span>
            </div>
            <h3 style={{ color: '#dc3545', marginBottom: '10px' }}>Error</h3>
            <p style={{ color: '#666' }}>{message}</p>
            
            <button
              onClick={() => window.location.reload()}
              style={{
                marginTop: '20px',
                padding: '12px 30px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Try Again
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <p style={{ marginTop: '30px', color: '#999', fontSize: '12px' }}>
        This is an automated report download system.
      </p>

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default QRReportDownload;